/** Pure shaping math for the signature touches (doc 10 backlog 3/4/5/7/13). No Pixi and no
 *  simulation state: the sim never reads anything here, so lockstep and saves are untouched. */

/** Floor cash reads as a growing heap: 1..5 bill layers by the pending bracket. */
export function pileLayers(pending: number): number {
    if (pending <= 0) return 0;
    if (pending < 150) return 1;
    if (pending < 400) return 2;
    if (pending < 900) return 3;
    if (pending < 2000) return 4;
    return 5;
}

/** The gold `+N` floater amount for a collection feedback line; null keeps the sentence floaters. */
export function collectedAmount(feedback: string): number | null {
    const match = /^Collected (\d+) bills? from/.exec(feedback);
    return match ? Number(match[1]) : null;
}

export const ATTACK_FRAMES = 6;
export const RELEASE_DECAY = 0.6;
export const FOLLOW_CORRECTION = 0.25;

export type FollowState = { ox: number; oy: number; vx: number; vy: number; mode: 'idle' | 'attack' | 'cruise' | 'release'; attack: number };
export const followIdle = (): FollowState => ({ ox: 0, oy: 0, vx: 0, vy: 0, mode: 'idle', attack: 0 });

/**
 * Attack/release shaping for the captain sprite following its sim position: velocity ramps in over
 * ATTACK_FRAMES and glides out with RELEASE_DECAY, so starts and stops read as weight while the
 * rendered position never strays far from the authoritative one (the correction keeps it bounded).
 */
export function stepFollow(f: FollowState, simVX: number, simVY: number): FollowState {
    let { ox, oy, vx, vy, mode, attack } = f;
    if (simVX !== 0 || simVY !== 0) {
        if (mode === 'idle' || mode === 'release') {
            mode = 'attack';
            attack = 0;
        }
        if (mode === 'attack') {
            attack++;
            const t = Math.min(1, attack / ATTACK_FRAMES), k = 1 - (1 - t) * (1 - t);
            vx = simVX * k;
            vy = simVY * k;
            if (t >= 1) mode = 'cruise';
        } else {
            vx = simVX;
            vy = simVY;
        }
    } else if (mode !== 'idle') {
        mode = 'release';
        vx *= RELEASE_DECAY;
        vy *= RELEASE_DECAY;
        if (Math.abs(vx) + Math.abs(vy) < 0.05) {
            mode = 'idle';
            vx = 0;
            vy = 0;
        }
    }
    // The offset carries the difference between the shaped visual velocity and the sim's own
    // velocity: lag builds during attack, closes during cruise and becomes the release glide.
    ox = (ox + vx - simVX) * (1 - FOLLOW_CORRECTION);
    oy = (oy + vy - simVY) * (1 - FOLLOW_CORRECTION);
    if (mode === 'idle' && Math.abs(ox) + Math.abs(oy) < 0.05) {
        ox = 0;
        oy = 0;
    }
    return { ox, oy, vx, vy, mode, attack };
}

/** One damped-spring step of the carry-stack lean toward `target` (px at the top layer). */
export function stepSway(sway: number, swayV: number, target: number): { sway: number; swayV: number } {
    swayV += (target - sway) * 0.3;
    swayV *= 0.78;
    sway += swayV;
    return { sway, swayV };
}
